import { useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  AUTOMATION_ACTIVE_BUTTON_LIMIT,
  AUTOMATION_CHAIN_DEPTH_LIMIT,
  AUTOMATION_COMMAND_KINDS,
  unsupportedAutomationTriggerReason,
  type AutomationCommand,
  type AutomationAdminReviewCommand,
  type AutomationAdminReviewResponse,
  type AutomationCommandDiagnostic,
  type AutomationCommandKind,
  type AutomationCommandLogEntry,
  type AutomationCommandLogResponse,
  type AutomationCommandVisibility,
  type AutomationReferenceKind,
  type AutomationRunOutcome,
  type AutomationUnsupportedTriggerReasonCode,
  type AutomationUnresolvedReferenceReason,
  type Board,
  type BoardMember,
  type CreateAutomationCommandBody,
  type DuplicateAutomationCommandBody,
  type DuplicateAutomationCommandResponse,
  type Label,
  type PublicProfile,
  type WorkspaceAutomationActivityEntry,
  type WorkspaceAutomationActivityResponse,
  type WorkspaceMember,
  type WorkspaceAutomationUsageResponse,
} from '@molt/shared';
import { Button, Dialog, Notice, profileName } from '@/components';
import { RelativeTime } from '@/components/RelativeTime';
import type { BoardFaceList } from '@molt/shared';
import {
  ACTION_LABEL_KEYS,
  AUTOMATION_COMMAND_LABEL_KEYS,
  AutomationEditor,
} from './AutomationEditor';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface BoardAutomationSectionProps {
  boardId: string;
  currentUserId?: string | null | undefined;
  lists: readonly BoardFaceList[];
  labels?: readonly Label[] | undefined;
  members?: readonly BoardMember[] | undefined;
  activityMembers?: readonly WorkspaceMember[] | undefined;
  canManage: boolean;
  disabledReason: string | null;
  readOnly?: boolean | undefined;
  readOnlyReason?: string | undefined;
  loading?: boolean | undefined;
  commands?: readonly AutomationCommand[] | undefined;
  commandsLoading?: boolean | undefined;
  labelsLoading?: boolean | undefined;
  membersLoading?: boolean | undefined;
  createBusy?: boolean | undefined;
  updateBusy?: boolean | undefined;
  deleteBusyId?: string | null | undefined;
  toggleBusyId?: string | null | undefined;
  duplicateBusyId?: string | null | undefined;
  duplicateResult?: DuplicateAutomationCommandResponse | null | undefined;
  logCommand?: AutomationCommandLogTarget | null | undefined;
  log?: AutomationCommandLogResponse | null | undefined;
  logLoading?: boolean | undefined;
  logError?: string | undefined;
  /*
    ⚠ THE THREE PROPS BELOW ARE THE "LOAD OLDER" CONTRACT WITH
    `BoardMenuPanel`, and they are stated here because the panel owns the
    paging state while this file owns the affordance (the pin file
    `automation-authoring.routing.test.ts` concatenates only the editor, THIS
    file and the schedule fields, so a routed phrase whose only call site sat
    in the panel would read as an orphan and fail).

    ⚠ **`log.nextCursor` IS THE ONLY SOURCE OF A CURSOR.** The codec is keyset
    over `(command_id, occurred_at)` and a fabricated cursor is refused with a
    422; the panel carries back what the server returned and NOTHING here
    constructs one. The affordance renders on `nextCursor !== null` alone —
    never on `entries.length`, because a full page can be the last page.

    ⚠ `olderError` is RENDERED, never swallowed. An affordance that eats the
    422 turns a refusal into an empty page, which is the same
    lies-rather-than-omits shape one level up that `BE-70.8` removed from the
    wire. It renders whatever the cursor says, so a failure is visible even
    once the button itself is gone.
  */
  olderLoading?: boolean | undefined;
  olderError?: string | undefined;
  onLoadOlder?: (() => void) | undefined;
  activity?: WorkspaceAutomationActivityResponse | null | undefined;
  activityLoading?: boolean | undefined;
  activityError?: string | undefined;
  activityBoardId?: string | null | undefined;
  activityOutcome?: AutomationRunOutcome | null | undefined;
  usage?: WorkspaceAutomationUsageResponse | null | undefined;
  usageLoading?: boolean | undefined;
  usageError?: string | undefined;
  canAdminReview?: boolean | undefined;
  adminDisabledReason?: string | null | undefined;
  adminReview?: AutomationAdminReviewResponse | null | undefined;
  adminReviewLoading?: boolean | undefined;
  adminReviewError?: string | undefined;
  adminBoardId?: string | null | undefined;
  adminDisableBusyId?: string | null | undefined;
  adminDefinitionTarget?: AutomationAdminReviewCommand | null | undefined;
  adminDefinition?: AutomationCommand | null | undefined;
  adminDefinitionLoading?: boolean | undefined;
  adminDefinitionError?: string | undefined;
  error?: string | undefined;
  editorError?: string | undefined;
  rowErrors?: Readonly<Record<string, string>> | undefined;
  adminRowErrors?: Readonly<Record<string, string>> | undefined;
  destinationBoards?: readonly Board[] | undefined;
  onCreate: (body: CreateAutomationCommandBody) => void;
  onUpdate: (commandId: string, body: CreateAutomationCommandBody) => void;
  onDelete: (commandId: string) => void;
  onToggleEnabled: (commandId: string, enabled: boolean) => void;
  onDuplicate: (commandId: string, body: DuplicateAutomationCommandBody) => void;
  onOpenLog: (command: AutomationCommandLogTarget) => void;
  onCloseLog: () => void;
  onActivityBoardChange: (boardId: string | null) => void;
  onActivityOutcomeChange: (outcome: AutomationRunOutcome | null) => void;
  onAdminBoardChange?: ((boardId: string | null) => void) | undefined;
  onOpenAdminDefinition?: ((command: AutomationAdminReviewCommand) => void) | undefined;
  onCloseAdminDefinition?: (() => void) | undefined;
  onAdminDisable?:
    ((commandId: string, boardId: string, reason?: string) => void) | undefined;
  onRetryActivity?: (() => void) | undefined;
  onRetryUsage?: (() => void) | undefined;
  onRetryAdminReview?: (() => void) | undefined;
  onRetryAdminDefinition?: (() => void) | undefined;
  onClearDuplicateResult?: (() => void) | undefined;
}

type AutomationPanelView = 'commands' | 'activity' | 'usage' | 'admin';
type AutomationCommandLogTarget = Pick<
  AutomationCommand,
  'id' | 'name' | 'commandKind' | 'enabled' | 'isBroken' | 'visibility' | 'updatedAt'
>;

const COMMAND_KIND_ORDER = AUTOMATION_COMMAND_KINDS;

/*
  ⚠ THE CAP IS IMPORTED, NOT RESTATED. `FR-AUTO-020` puts the number 20 in
  the requirement. Earlier slices carried separate frontend and server literals
  that could drift without either side going red: the authoring UI would warn at
  one number while the server refused at another. The shared constant is now the
  authority (`packages/shared/src/automation.ts`, beside
  `AUTOMATION_ACTION_LIMIT` and `AUTOMATION_CHAIN_DEPTH_LIMIT`), and both the
  Automation tab and server refusal path import it.
*/

/*
  ⚠ EVERY TABLE BELOW HOLDS A KEY, NOT A WORD. A module-level constant has no
  context to call `t()` from, so each is reached at render as
  `t(TABLE[wireValue])` — the `BOARD_ROLE_LABELS` shape. None of them is
  visible to the `t('…')` anchor; `automation-authoring.routing.test.ts` holds
  them by their string constants and by arm-for-arm audits against the shared
  enum constants, so a tenth diagnostic reason fails a test rather than shipping
  a snake_case word into a `Notice` title.
*/

const STARTER_KEYS: Record<AutomationCommandKind, string> = {
  rule: 'automationAuthoring.starter.rule',
  card_button: 'automationAuthoring.starter.card_button',
  board_button: 'automationAuthoring.starter.board_button',
  calendar_command: 'automationAuthoring.starter.calendar_command',
  due_date_command: 'automationAuthoring.starter.due_date_command',
};

/*
  ⚠ A NOUN IN A SLOT, KEPT AS A SLOT. These five feed three sentences — the
  create button, the empty state and the button-limit title — as `{kind}`. Five
  keys and three sentence keys, not fifteen sentence keys, on the ruling that a
  mid-sentence NOUN survives a slot where a verb stem does not.
*/
const COMMAND_KIND_SINGULAR_KEYS: Record<AutomationCommandKind, string> = {
  rule: 'automationAuthoring.commandKindSingular.rule',
  card_button: 'automationAuthoring.commandKindSingular.card_button',
  board_button: 'automationAuthoring.commandKindSingular.board_button',
  calendar_command: 'automationAuthoring.commandKindSingular.calendar_command',
  due_date_command: 'automationAuthoring.commandKindSingular.due_date_command',
};

/*
  ⚠ ONE KEY SET FOR TWO LISTS, DELIBERATELY. `ACTIVITY_ENTRY_LABELS` WAS
  `LOG_ENTRY_LABELS` — the same object, aliased — because it is one vocabulary
  rendered by one file into two lists of the same shape. Keyed by
  `AUTOMATION_RUN_ENTRY_KINDS`.
*/
const ENTRY_KIND_KEYS: Record<AutomationCommandLogEntry['entryKind'], string> = {
  run: 'automationAuthoring.entryKind.run',
  quota_blocked: 'automationAuthoring.entryKind.quota_blocked',
  permission_blocked: 'automationAuthoring.entryKind.permission_blocked',
  missing_reference_blocked: 'automationAuthoring.entryKind.missing_reference_blocked',
  cap_exceeded: 'automationAuthoring.entryKind.cap_exceeded',
  loop_suppressed: 'automationAuthoring.entryKind.loop_suppressed',
  definition_changed: 'automationAuthoring.entryKind.definition_changed',
  admin_disabled: 'automationAuthoring.entryKind.admin_disabled',
};

/*
  ⚠ THE RAW WIRE VALUE WAS DRAWN AS PROSE HERE (`outcome ?? 'Recorded'`). It is
  now keyed by `AUTOMATION_RUN_OUTCOMES`, byte-identically — still LOWER CASE —
  so nothing moves. Deliberately NOT the same set as `OUTCOME_FILTER_KEYS`: the
  filter options are title case and `warning` reads `Warnings` there, so the two
  vocabularies already disagree in English.
*/
const OUTCOME_KEYS: Record<AutomationRunOutcome, string> = {
  succeeded: 'automationAuthoring.outcome.succeeded',
  warning: 'automationAuthoring.outcome.warning',
  failed: 'automationAuthoring.outcome.failed',
};

const DIAGNOSTIC_REASON_KEYS: Record<AutomationCommandDiagnostic['reason'], string> = {
  disabled: 'automationAuthoring.diagnostic.reason.disabled',
  quota_exhausted: 'automationAuthoring.diagnostic.reason.quota_exhausted',
  condition_excluded: 'automationAuthoring.diagnostic.reason.condition_excluded',
  permission_denied: 'automationAuthoring.diagnostic.reason.permission_denied',
  missing_reference: 'automationAuthoring.diagnostic.reason.missing_reference',
  cap_exceeded: 'automationAuthoring.diagnostic.reason.cap_exceeded',
  loop_suppressed: 'automationAuthoring.diagnostic.reason.loop_suppressed',
  acting_member_missing: 'automationAuthoring.diagnostic.reason.acting_member_missing',
  admin_disabled: 'automationAuthoring.diagnostic.reason.admin_disabled',
};

const REFERENCE_KIND_KEYS: Record<AutomationReferenceKind, string> = {
  board: 'automationAuthoring.copy.referenceKind.board',
  list: 'automationAuthoring.copy.referenceKind.list',
  label: 'automationAuthoring.copy.referenceKind.label',
  member: 'automationAuthoring.copy.referenceKind.member',
  card: 'automationAuthoring.copy.referenceKind.card',
  checklist: 'automationAuthoring.copy.referenceKind.checklist',
  checklist_item: 'automationAuthoring.copy.referenceKind.checklist_item',
  custom_field: 'automationAuthoring.copy.referenceKind.custom_field',
};

const UNRESOLVED_REASON_KEYS: Record<AutomationUnresolvedReferenceReason, string> = {
  not_found_on_destination_board:
    'automationAuthoring.copy.unresolvedReason.not_found_on_destination_board',
  ambiguous_on_destination_board:
    'automationAuthoring.copy.unresolvedReason.ambiguous_on_destination_board',
  not_copyable_to_destination_board:
    'automationAuthoring.copy.unresolvedReason.not_copyable_to_destination_board',
};

const UNSUPPORTED_TRIGGER_REASON_KEYS: Record<
  AutomationUnsupportedTriggerReasonCode,
  string
> = {
  no_activity_event:
    'automationAuthoring.editor.trigger.unsupportedReason.no_activity_event',
  no_completion_aggregate:
    'automationAuthoring.editor.trigger.unsupportedReason.no_completion_aggregate',
};

const VISIBILITY_KEYS: Record<AutomationCommandVisibility, string> = {
  shared: 'automationAuthoring.definition.visibilityValue.shared',
  private: 'automationAuthoring.definition.visibilityValue.private',
};

const OUTCOME_FILTERS: { key: string; value: AutomationRunOutcome | null }[] = [
  { key: 'automationAuthoring.activity.outcomeFilter.all', value: null },
  { key: 'automationAuthoring.activity.outcomeFilter.succeeded', value: 'succeeded' },
  { key: 'automationAuthoring.activity.outcomeFilter.warning', value: 'warning' },
  { key: 'automationAuthoring.activity.outcomeFilter.failed', value: 'failed' },
];

const PANEL_VIEW_KEYS: Record<AutomationPanelView, string> = {
  commands: 'automationAuthoring.view.commands',
  activity: 'automationAuthoring.view.activity',
  usage: 'automationAuthoring.view.usage',
  admin: 'automationAuthoring.view.admin',
};

const PANEL_VIEW_ORDER: readonly AutomationPanelView[] = [
  'commands',
  'activity',
  'usage',
  'admin',
];

function entryTone(entry: AutomationCommandLogEntry): 'bad' | 'warn' | 'info' {
  if (entry.outcome === 'failed') return 'bad';
  if (entry.outcome === 'warning' || entry.entryKind !== 'run') return 'warn';
  return 'info';
}

function activityEntryTone(
  entry: WorkspaceAutomationActivityEntry,
): 'bad' | 'warn' | 'info' {
  if (entry.outcome === 'failed') return 'bad';
  if (entry.outcome === 'warning' || entry.entryKind !== 'run') return 'warn';
  return 'info';
}

/**
 * ⚠ RETURNS A KEY AND ITS SLOT VALUES. A module function cannot call `t`, so
 * every helper below hands the render a key rather than a sentence.
 *
 * ⚠ **`{card}` USED TO BE A RAW ULID AND NO LONGER IS.** The log route now
 * projects `entries[].card` — a ref carrying the card's `title`, its
 * `shortLink` and a `state` — so `entryTarget` fills the slot with the title
 * (`FE-70.2`, `FR-AUTO-013` AC1, *"each naming the affected card"*). The
 * *activity* helper below still fills its slot with an id: that is
 * `GET /workspaces/:id/automation-activity`, `FR-AUTO-014`'s route, whose
 * projection this chunk does not own. The two are deliberately no longer
 * symmetric.
 */
interface AutomationLine {
  readonly key: string;
  readonly values?: Record<string, string | number>;
}

function unsupportedTriggerLine(command: AutomationCommand): AutomationLine | null {
  if (command.commandKind !== 'rule' || command.trigger === null) return null;
  const reason = unsupportedAutomationTriggerReason(command.trigger.triggerKind);
  return reason === null ? null : { key: UNSUPPORTED_TRIGGER_REASON_KEYS[reason] };
}

/*
  ⚠ A RESOLVED MEMBER IS PRODUCT DATA AND A MISSING ONE IS CHROME. The display
  name goes into the `{actor}` slot verbatim; the `User {user}` fallback is a
  catalogue sentence. Keeping the two arms apart is what stops a display name
  being looked up as a message key.
*/
type ActorLine = { readonly displayName: string } | AutomationLine;

/*
  ⚠ FOUR OUTCOMES, AND `null` IS NOT "no card". `null` here means **nothing is
  known about a target at all**, and the caller renders no target line.

  - `cardId === null` — the run had no card (a board button, a board-scoped
    rule). `boardRun`, exactly as before.
  - a card with a title — `cardTarget`, filled with the TITLE. `state` is
    `'live'`, `'archived'` or `'deleted'` and all three are named: the log is
    history, and naming what a run acted on is the requirement. The dialog
    renders no links, so D4's "deleted is named but not linked" needs no second
    branch here.
  - `state === 'unreadable'` — the card has since moved to a board this reader
    was never authorised to see, and the route withholds its title rather than
    becoming a cross-board title oracle. **It must NOT degrade to the id** —
    that is the whole of `FE-70.2`. It now renders `log.cardWithheld`, a
    withholding phrase that says a target EXISTS and is not shown, names
    nothing, and does not say why: "moved to another board" would itself leak
    where the card went. `values` is deliberately absent — there is no slot,
    because there is nothing to put in one.
  - anything else with no title — a body whose `card` is absent altogether:
    `null`, and no line. The field is `.optional()` on the client arm, so a
    legacy body carries no `card` at all, and *that is not evidence of
    withholding*. Claiming a card was withheld when none was ever recorded
    would be its own small lie.

  ⚠ **THE WITHHELD ARM KEYS ON `state`, NOT ON `title === null`.** The two were
  the same condition until this chunk and are not the same fact: `title` is
  null both when the route withholds a name and when the body has no `card`
  key. The `.superRefine` on `automationLogCardRefSchema` makes
  `title === null` ⟺ `state === 'unreadable'` *within a present ref*, which is
  exactly why the discriminant is the ref's own state and the absence of the
  ref is the fourth arm.
*/
function entryTarget(entry: AutomationCommandLogEntry): AutomationLine | null {
  if (entry.cardId === null) return { key: 'automationAuthoring.log.boardRun' };
  const card = entry.card ?? null;
  if (card === null) return null;
  if (card.state === 'unreadable') {
    return { key: 'automationAuthoring.log.cardWithheld' };
  }
  return card.title === null
    ? null
    : { key: 'automationAuthoring.log.cardTarget', values: { card: card.title } };
}

/**
 * One action as the log union hands it over.
 *
 * ⚠ **THE TWO ARMS OF `AutomationCommandLogEntry` CARRY DIFFERENT ACTION
 * SHAPES, AND THAT IS DELIBERATE.** The *run* arm's actions are
 * `automationLogActionSchema` — the shipped base intersected with
 * `{ httpStatus?: number | null }`. The *definition-change* arm keeps the base
 * schema, because a definition change makes no HTTP call and a field that can
 * never be filled would be a field that lies. So the indexed access below is a
 * union in which only one member declares `httpStatus`, and a renderer must
 * narrow before it reads it.
 */
type AutomationLogEntryAction = AutomationCommandLogEntry['actions'][number];

/*
  ⚠ `FE-71.3` — TWO NARROWINGS, AND BOTH ARE LOAD-BEARING.

  `'httpStatus' in action` is the arm narrowing: without it this does not
  compile, because the definition-change arm's action type has no such
  property. It is not a defensive `in` over a field that is always there.

  `typeof status === 'number'` is the value narrowing, and it is NOT
  `status !== null`. The client parses the log with the PERMISSIVE twin, where
  `httpStatus` is `.optional()`, so the value is `number | null | undefined` —
  and `undefined !== null` is true, which would send an empty slot into
  `interfaceMessage` and throw into the render.

  ⚠ **A 200 IS NEVER INVENTED.** Successful HTTP actions now store the returned
  status in `parameters_snapshot.httpStatus`, the same field failures use.
  Absence still renders as absence for rows that truly recorded no status.
*/
function actionStatusLine(action: AutomationLogEntryAction): AutomationLine | null {
  const status = 'httpStatus' in action ? action.httpStatus : undefined;
  return typeof status === 'number'
    ? { key: 'automationAuthoring.log.actionHttpStatus', values: { status } }
    : null;
}

function activityEntryTarget(entry: WorkspaceAutomationActivityEntry): AutomationLine {
  return entry.cardId === null
    ? { key: 'automationAuthoring.activity.boardRun' }
    : { key: 'automationAuthoring.activity.cardTarget', values: { card: entry.cardId } };
}

function userFallbackLine(userId: string | null): AutomationLine {
  return userId === null
    ? { key: 'automationAuthoring.admin.noActingMember' }
    : { key: 'automationAuthoring.admin.userFallback', values: { user: userId } };
}

/*
  ⚠ `profileName(…)` is a DISPLAY NAME — product data, and it goes into the
  `{actor}` slot of a routed sentence rather than being composed with English
  around it. The unresolved arm is a key too, because `User {user}` is chrome.

  ⚠ **THE SERVER NAMES THE ACTOR NOW, AND THE BOARD MEMBER LIST IS NO LONGER
  ASKED FIRST.** `entries[].actor` is a `publicProfileSchema` resolved by a
  `LEFT JOIN users` with **no** join to `board_members`, so it survives the
  actor leaving the board — which is exactly what the old lookup could not do.
  Searching `members` first would reintroduce the defect for a removed member
  while looking correct for everyone still on the board.

  ⚠ **THE TWO TIERS BELOW ARE LEGACY-BODY TIERS, NOT FALLBACKS FOR MISSING
  DATA.** Against this server they are unreachable: `actorUserId` is
  `ON DELETE SET NULL` against `users` and `users.display_name` is `NOT NULL`,
  so a non-null `actorUserId` provably resolves, and the route `.parse()`s its
  own projection with `automationCommandLogResponseStrictSchema`, where `actor`
  is required. They exist because the *client* arm declares `actor` optional, so
  a body from a server older than this contract still has to render something
  other than a crash — and `automationAuthoring.log.userFallback` is that last
  tier. It is not a name this chunk's server can produce.
*/
function entryActor(
  entry: AutomationCommandLogEntry,
  members: readonly BoardMember[],
): ActorLine | null {
  if (entry.actorUserId === null) return null;
  if (entry.actor !== undefined && entry.actor !== null) {
    return { displayName: profileName(entry.actor) };
  }
  const member = members.find((candidate) => candidate.id === entry.actorUserId);
  return member === undefined
    ? {
        key: 'automationAuthoring.log.userFallback',
        values: { user: entry.actorUserId },
      }
    : { displayName: profileName(member) };
}

function activityEntryActor(
  entry: WorkspaceAutomationActivityEntry,
  members: readonly PublicProfile[],
): ActorLine | null {
  if (entry.actorUserId === null) return null;
  const member = members.find((candidate) => candidate.id === entry.actorUserId);
  return member === undefined
    ? {
        key: 'automationAuthoring.activity.userFallback',
        values: { user: entry.actorUserId },
      }
    : { displayName: profileName(member) };
}

function entryActorLineKey(entry: AutomationCommandLogEntry): string {
  if (entry.entryKind === 'definition_changed') {
    return 'automationAuthoring.log.actorChanged';
  }
  if (entry.entryKind === 'admin_disabled') {
    return 'automationAuthoring.log.actorDisabled';
  }
  return 'automationAuthoring.log.actor';
}

function formatCounter(value: number): string {
  return value.toLocaleString('en-GB');
}

type QuotaMetricId = 'runs' | 'operations' | 'emails';

interface QuotaMetric {
  id: QuotaMetricId;
  used: number;
  limit: number;
}

interface QuotaMetricState extends QuotaMetric {
  remaining: number;
  percent: number;
  exhausted: boolean;
  warning: boolean;
}

const QUOTA_METRIC_KEYS: Record<QuotaMetricId, string> = {
  runs: 'automationAuthoring.usage.metric.runs',
  operations: 'automationAuthoring.usage.metric.operations',
  emails: 'automationAuthoring.usage.metric.emails',
};

/**
 * ⚠ THREE PLURAL SENTENCES, ONE PER METRIC, AND THE `toLowerCase()` IS GONE.
 * The running-low body used to splice `warning.label.toLowerCase()` into one
 * sentence, which is untranslatable twice over — an English lower-case
 * transform, and a noun in a slot that the sentence around it cannot
 * pluralise, so `1 runs remaining this month.` was the live English bug at a
 * remaining count of one. Choosing the sentence by metric id deletes all three
 * problems, and costs one key fewer than a lower-case noun table would have.
 */
const QUOTA_RUNNING_LOW_KEYS: Record<QuotaMetricId, string> = {
  runs: 'automationAuthoring.usage.runningLowBody.runs',
  operations: 'automationAuthoring.usage.runningLowBody.operations',
  emails: 'automationAuthoring.usage.runningLowBody.emails',
};

function quotaMetrics(usage: WorkspaceAutomationUsageResponse): QuotaMetricState[] {
  const metrics: QuotaMetric[] = [
    { id: 'runs', used: usage.runsCount, limit: usage.limits.runs },
    {
      id: 'operations',
      used: usage.operationsCount,
      limit: usage.limits.operations,
    },
    { id: 'emails', used: usage.emailsSentCount, limit: usage.limits.emails },
  ];

  return metrics.map((metric) => {
    const remaining = Math.max(0, metric.limit - metric.used);
    const percent =
      metric.limit <= 0 ? 100 : Math.min(100, (metric.used / metric.limit) * 100);
    return {
      ...metric,
      remaining,
      percent,
      exhausted: metric.used >= metric.limit,
      warning: metric.used >= metric.limit * 0.9 && metric.used < metric.limit,
    };
  });
}

function formatResetDate(resetAt: string): string {
  const date = new Date(resetAt);
  if (Number.isNaN(date.getTime())) return resetAt;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * ⚠ THE DISCRIMINANT EXISTS BECAUSE THE USAGE PANEL BRANCHED ON DISPLAY COPY.
 * `quotaSummaryView?.title === 'Monthly run quota exhausted'` was control flow
 * against a rendered sentence: route the title and the `role="alert"` `Notice`
 * silently stops rendering, failing as a MISSING ELEMENT rather than as a
 * translation. `kind` is what the comparison reads now, and the sentences it
 * names are resolved at render.
 */
interface QuotaSummary {
  readonly kind: 'runsExhausted' | 'metricExhausted' | 'runningLow' | 'healthy';
  readonly titleKey: string;
  readonly bodyKey: string;
  readonly metricId: QuotaMetricId | null;
  readonly remaining: number | null;
  readonly className: string;
}

function quotaSummary(metrics: readonly QuotaMetricState[]): QuotaSummary {
  const runMetric = metrics.find((metric) => metric.id === 'runs');
  const exhausted = metrics.find((metric) => metric.exhausted);
  if (runMetric?.exhausted) {
    return {
      kind: 'runsExhausted',
      titleKey: 'automationAuthoring.usage.runsExhaustedTitle',
      bodyKey: 'automationAuthoring.usage.runsExhaustedBody',
      metricId: null,
      remaining: null,
      className: 'border-bad bg-bad/10 text-bad',
    };
  }
  if (exhausted !== undefined) {
    return {
      kind: 'metricExhausted',
      titleKey: 'automationAuthoring.usage.metricExhaustedTitle',
      bodyKey: 'automationAuthoring.usage.metricExhaustedBody',
      metricId: exhausted.id,
      remaining: null,
      className: 'border-bad bg-bad/10 text-bad',
    };
  }
  const warning = metrics.find((metric) => metric.warning);
  if (warning !== undefined) {
    return {
      kind: 'runningLow',
      titleKey: 'automationAuthoring.usage.runningLowTitle',
      bodyKey: QUOTA_RUNNING_LOW_KEYS[warning.id],
      metricId: warning.id,
      remaining: warning.remaining,
      className: 'border-warn bg-warn/10 text-warn',
    };
  }
  return {
    kind: 'healthy',
    titleKey: 'automationAuthoring.usage.quotaTitle',
    bodyKey:
      runMetric === undefined
        ? 'automationAuthoring.usage.quotaBodyFallback'
        : 'automationAuthoring.usage.quotaBody',
    metricId: null,
    remaining: runMetric?.remaining ?? null,
    className: 'border-border-strong bg-surface-raised text-ink',
  };
}

function quotaMetricTone(metric: QuotaMetricState): string {
  if (metric.exhausted) return 'border-bad bg-bad/10 text-bad';
  if (metric.warning) return 'border-warn bg-warn/10 text-warn';
  return 'border-border-strong bg-surface-raised text-ink';
}

function automationTabClassName(disabled = false): string {
  return [
    'relative inline-flex h-7 items-center justify-center gap-2 rounded-card border px-2 text-sm font-medium transition-[filter]',
    'hover:brightness-95 dark:hover:brightness-110',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    disabled ? 'cursor-not-allowed opacity-60' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

function automationTabStateClassName(selected: boolean): string {
  return selected
    ? 'border-accent bg-accent text-accent-ink'
    : 'border-border-strong bg-surface-raised text-ink-muted';
}

function commandsForKind(
  commands: readonly AutomationCommand[],
  kind: AutomationCommandKind,
): AutomationCommand[] {
  return commands.filter((command) => command.commandKind === kind);
}

function isButtonKind(
  kind: AutomationCommandKind,
): kind is 'card_button' | 'board_button' {
  return kind === 'card_button' || kind === 'board_button';
}

/**
 * ⚠ TWO WHOLE SENTENCES, NOT ONE WITH A `card`/`board` STEM SPLICED IN. The
 * stem was an English adjective mid-sentence; a slot there is untranslatable.
 * Returns a KEY — a module function cannot call `t`.
 */
function activeButtonLimitKey(kind: AutomationCommandKind): string | null {
  if (!isButtonKind(kind)) return null;
  return kind === 'card_button'
    ? 'automationAuthoring.commands.buttonLimitCard'
    : 'automationAuthoring.commands.buttonLimitBoard';
}

function activeButtonCountsByOwner(
  commands: readonly AutomationCommand[],
  kind: AutomationCommandKind,
): Map<string, number> {
  const counts = new Map<string, number>();
  if (!isButtonKind(kind)) return counts;
  for (const command of commands) {
    if (command.commandKind !== kind || !command.enabled) continue;
    counts.set(command.createdByUserId, (counts.get(command.createdByUserId) ?? 0) + 1);
  }
  return counts;
}

export function BoardAutomationSection({
  boardId,
  currentUserId = null,
  lists,
  labels = [],
  members = [],
  activityMembers,
  canManage,
  disabledReason,
  readOnly = false,
  /*
    ⚠ NO DEFAULT — a default prop value cannot call `t`. The render reads
    `readOnlyReason ?? t('…commands.readOnlyDefault')` instead.
    `BoardMenuPanel` already passes a resolved string and is unaffected.
  */
  readOnlyReason,
  loading = false,
  commands,
  commandsLoading = false,
  labelsLoading = false,
  membersLoading = false,
  createBusy = false,
  updateBusy = false,
  deleteBusyId = null,
  toggleBusyId = null,
  duplicateBusyId = null,
  duplicateResult = null,
  logCommand = null,
  log = null,
  logLoading = false,
  logError,
  olderLoading = false,
  olderError,
  activity = null,
  activityLoading = false,
  activityError,
  activityBoardId = null,
  activityOutcome = null,
  usage = null,
  usageLoading = false,
  usageError,
  canAdminReview = false,
  adminDisabledReason = null,
  adminReview = null,
  adminReviewLoading = false,
  adminReviewError,
  adminBoardId = null,
  adminDisableBusyId = null,
  adminDefinitionTarget = null,
  adminDefinition = null,
  adminDefinitionLoading = false,
  adminDefinitionError,
  error,
  editorError,
  rowErrors = {},
  adminRowErrors = {},
  destinationBoards = [],
  onCreate,
  onUpdate,
  onDelete,
  onToggleEnabled,
  onDuplicate,
  onOpenLog,
  onCloseLog,
  onLoadOlder,
  onActivityBoardChange,
  onActivityOutcomeChange,
  onAdminBoardChange,
  onOpenAdminDefinition,
  onCloseAdminDefinition,
  onAdminDisable,
  onRetryActivity,
  onRetryUsage,
  onRetryAdminReview,
  onRetryAdminDefinition,
  onClearDuplicateResult,
}: BoardAutomationSectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const automationActivityMembers = activityMembers ?? members;
  /*
    ⚠ READ INTO A LOCAL BECAUSE `log?.nextCursor !== null` IS TRUE WHEN THERE
    IS NO LOG. Optional chaining yields `undefined` on an unloaded dialog, and
    `undefined !== null` renders the "load older" affordance over an empty
    dialog that has nothing to page back from. `?? null` folds absent and
    "last page" into the one state that means "do not offer another page".
  */
  const olderCursor = log?.nextCursor ?? null;
  const [activeView, setActiveView] = useState<AutomationPanelView>('commands');
  const [activeKind, setActiveKind] = useState<AutomationCommandKind>('rule');
  const [editorCommand, setEditorCommand] = useState<AutomationCommand | null>(null);
  const [editorKind, setEditorKind] = useState<AutomationCommandKind | null>(null);
  const [editorSubmitted, setEditorSubmitted] = useState(false);
  const [editorObservedBusy, setEditorObservedBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AutomationCommand | null>(null);
  const [copyTarget, setCopyTarget] = useState<AutomationCommand | null>(null);
  const [adminDisableTarget, setAdminDisableTarget] =
    useState<AutomationAdminReviewCommand | null>(null);
  const [adminDisableReason, setAdminDisableReason] = useState('');
  const [copyName, setCopyName] = useState('');
  const [copyDestinationBoardId, setCopyDestinationBoardId] = useState('');
  const commandRows = useMemo(
    () => commandsForKind(commands ?? [], activeKind),
    [activeKind, commands],
  );
  const activeButtonOwnerCounts = useMemo(
    () => activeButtonCountsByOwner(commands ?? [], activeKind),
    [activeKind, commands],
  );
  const activeButtonLimitReached =
    currentUserId !== null &&
    (activeButtonOwnerCounts.get(currentUserId) ?? 0) >= AUTOMATION_ACTIVE_BUTTON_LIMIT;
  const activeButtonNoticeKey = activeButtonLimitKey(activeKind);
  const editorOpen = editorKind !== null;
  const editorBusy = createBusy || updateBusy;
  const metadataLoading = labelsLoading || membersLoading;
  const checking = loading || commandsLoading;
  /*
    ⚠ A COMPUTED KEY, NOT A COMPUTED SENTENCE, AND IT HAS THREE USE SITES: the
    locked `Notice` in the commands panel, the disabled tab's `title`, and the
    Create button's `disabledReason`. `disabledReason` arrives already resolved
    from the caller, so the pair keeps a `text` arm beside the key arm.
  */
  const blocked: AutomationLine | { readonly text: string } | null = checking
    ? { key: 'automationAuthoring.commands.blockedChecking' }
    : canManage
      ? null
      : disabledReason !== null
        ? { text: disabledReason }
        : { key: 'automationAuthoring.commands.blockedNotAdmin' };
  const blockedReason =
    blocked === null ? null : 'text' in blocked ? blocked.text : t(blocked.key);
  const managementEnabled = canManage && !readOnly;
  const activityBoardOptions = useMemo(() => {
    const rows = new Map<string, string>();
    for (const board of destinationBoards) {
      rows.set(board.id, board.title);
    }
    if (!rows.has(boardId)) {
      rows.set(boardId, t('automationAuthoring.activity.thisBoard'));
    }
    for (const entry of activity?.entries ?? []) {
      rows.set(entry.board.id, entry.board.name);
    }
    return [...rows.entries()].map(([id, name]) => ({ id, name }));
  }, [activity?.entries, boardId, destinationBoards, t]);
  const adminBoardOptions = useMemo(() => {
    const rows = new Map<string, string>();
    for (const board of destinationBoards) {
      rows.set(board.id, board.title);
    }
    for (const command of adminReview?.commands ?? []) {
      rows.set(command.board.id, command.board.name);
    }
    if (!rows.has(boardId)) {
      rows.set(boardId, t('automationAuthoring.admin.thisBoard'));
    }
    return [...rows.entries()].map(([id, name]) => ({ id, name }));
  }, [adminReview?.commands, boardId, destinationBoards, t]);
  const quotaMetricRows = useMemo(
    () => (usage === null ? [] : quotaMetrics(usage)),
    [usage],
  );
  const quotaSummaryView =
    quotaMetricRows.length === 0 ? null : quotaSummary(quotaMetricRows);
  const quotaResetText =
    usage === null
      ? null
      : t('automationAuthoring.usage.resetText', {
          date: formatResetDate(usage.resetAt),
        });

  function rowLimitReason(kind: AutomationCommandKind): string | undefined {
    const key = activeButtonLimitKey(kind);
    return key === null ? undefined : t(key, { limit: AUTOMATION_ACTIVE_BUTTON_LIMIT });
  }

  function openCreate(kind: AutomationCommandKind): void {
    setEditorCommand(null);
    setEditorKind(kind);
    setEditorSubmitted(false);
    setEditorObservedBusy(false);
  }

  function openEdit(command: AutomationCommand): void {
    setEditorCommand(command);
    setEditorKind(command.commandKind);
    setEditorSubmitted(false);
    setEditorObservedBusy(false);
  }

  function openCopy(command: AutomationCommand): void {
    setCopyTarget(command);
    setCopyName('');
    setCopyDestinationBoardId('');
    onClearDuplicateResult?.();
  }

  function closeCopy(): void {
    setCopyTarget(null);
    setCopyName('');
    setCopyDestinationBoardId('');
    onClearDuplicateResult?.();
  }

  function closeEditor(): void {
    setEditorCommand(null);
    setEditorKind(null);
    setEditorSubmitted(false);
    setEditorObservedBusy(false);
  }

  function submitEditor(body: CreateAutomationCommandBody): void {
    setEditorSubmitted(true);
    if (editorCommand === null) {
      onCreate(body);
      return;
    }
    onUpdate(editorCommand.id, body);
  }

  function submitCopy(): void {
    if (copyTarget === null) return;
    const trimmedName = copyName.trim();
    onDuplicate(copyTarget.id, {
      destinationBoardId: copyDestinationBoardId === '' ? null : copyDestinationBoardId,
      ...(trimmedName === '' ? {} : { name: trimmedName }),
    });
  }

  function openAdminDisable(command: AutomationAdminReviewCommand): void {
    setAdminDisableTarget(command);
    setAdminDisableReason('');
  }

  function closeAdminDisable(): void {
    setAdminDisableTarget(null);
    setAdminDisableReason('');
  }

  function submitAdminDisable(): void {
    if (adminDisableTarget === null) return;
    const reason = adminDisableReason.trim();
    onAdminDisable?.(
      adminDisableTarget.id,
      adminDisableTarget.board.id,
      reason === '' ? undefined : reason,
    );
    closeAdminDisable();
  }

  useEffect(() => {
    if (!editorSubmitted) return;
    if (editorBusy) {
      setEditorObservedBusy(true);
      return;
    }
    if (editorObservedBusy && editorError === undefined) {
      setEditorCommand(null);
      setEditorKind(null);
      setEditorSubmitted(false);
      setEditorObservedBusy(false);
    }
  }, [editorBusy, editorError, editorObservedBusy, editorSubmitted]);

  useEffect(() => {
    if (!canManage && activeView === 'commands') {
      setActiveView('activity');
    }
    if (!canAdminReview && activeView === 'admin') {
      setActiveView(canManage ? 'commands' : 'activity');
    }
  }, [activeView, canAdminReview, canManage]);

  return (
    <section
      className="flex flex-col gap-4"
      aria-label={t('automationAuthoring.section.label')}
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-ink">
          {t('automationAuthoring.section.heading')}
        </h3>
        <p className="text-sm text-ink-muted">
          {t('automationAuthoring.section.description')}
        </p>
      </div>

      {usageLoading ? (
        <Notice tone="info" body={t('automationAuthoring.usage.loading')} />
      ) : null}
      {usageError !== undefined ? (
        <Notice
          tone="bad"
          title={t('automationAuthoring.usage.loadFailedTitle')}
          body={usageError}
          action={
            onRetryUsage === undefined ? undefined : (
              <Button type="button" size="sm" onClick={onRetryUsage}>
                {t('automationAuthoring.retry')}
              </Button>
            )
          }
        />
      ) : null}
      {quotaSummaryView === null ? null : (
        <section
          aria-label={t('automationAuthoring.usage.summaryLabel')}
          className={`rounded-card border p-3 ${quotaSummaryView.className}`}
        >
          <h4 className="text-sm font-semibold">
            {t(
              quotaSummaryView.titleKey,
              quotaSummaryView.metricId === null
                ? undefined
                : { metric: t(QUOTA_METRIC_KEYS[quotaSummaryView.metricId]) },
            )}
          </h4>
          {/*
            ⚠ THE COUNT IS THE THIRD ARGUMENT. `interfaceMessage` selects
            `forms.other` whenever `count !== 1`, so dropping it renders the
            plural always — invisible at 993 and wrong at 1.
          */}
          <p className="mt-1 text-sm">
            {quotaSummaryView.remaining === null
              ? t(quotaSummaryView.bodyKey)
              : t(
                  quotaSummaryView.bodyKey,
                  { remaining: formatCounter(quotaSummaryView.remaining) },
                  quotaSummaryView.remaining,
                )}
          </p>
          {quotaResetText === null ? null : (
            <p className="mt-1 text-xs opacity-80">{quotaResetText}</p>
          )}
        </section>
      )}

      <div
        role="tablist"
        aria-label={t('automationAuthoring.view.viewsLabel')}
        className="flex flex-wrap gap-1"
      >
        {PANEL_VIEW_ORDER.map((view) => {
          const disabled =
            (view === 'commands' && blockedReason !== null) ||
            (view === 'admin' && !canAdminReview);
          const disabledTitle =
            view === 'commands'
              ? (blockedReason ?? undefined)
              : view === 'admin'
                ? (adminDisabledReason ?? undefined)
                : undefined;
          return (
            <button
              key={view}
              type="button"
              className={`${automationTabClassName(disabled)} ${automationTabStateClassName(activeView === view)}`}
              role="tab"
              aria-selected={activeView === view}
              aria-controls={`automation-view-${view}`}
              disabled={disabled}
              title={disabled ? disabledTitle : undefined}
              onClick={() => setActiveView(view)}
            >
              {t(PANEL_VIEW_KEYS[view])}
            </button>
          );
        })}
      </div>

      {error !== undefined ? <Notice tone="bad" body={error} /> : null}
      {readOnly ? (
        <Notice
          tone="info"
          body={readOnlyReason ?? t('automationAuthoring.commands.readOnlyDefault')}
        />
      ) : null}
      {metadataLoading && activeView === 'commands' ? (
        <Notice tone="info" body={t('automationAuthoring.commands.metadataLoading')} />
      ) : null}

      <div
        id={`automation-view-commands`}
        role="tabpanel"
        hidden={activeView !== 'commands'}
        className={activeView === 'commands' ? 'flex flex-col gap-3' : 'hidden'}
      >
        {blockedReason !== null ? <Notice tone="locked" body={blockedReason} /> : null}
        <div
          role="tablist"
          aria-label={t('automationAuthoring.view.kindsLabel')}
          className="flex flex-wrap gap-1"
        >
          {COMMAND_KIND_ORDER.map((kind) => (
            <button
              key={kind}
              type="button"
              className={`${automationTabClassName()} ${automationTabStateClassName(activeKind === kind)}`}
              role="tab"
              aria-selected={activeKind === kind}
              aria-controls={`automation-tab-${kind}`}
              onClick={() => setActiveKind(kind)}
            >
              {t(AUTOMATION_COMMAND_LABEL_KEYS[kind])}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="min-w-0 text-sm font-semibold text-ink">
            {t(AUTOMATION_COMMAND_LABEL_KEYS[activeKind])}
          </h4>
          {!readOnly ? (
            <Button
              type="button"
              size="sm"
              variant="primary"
              className="self-start"
              disabled={blockedReason !== null}
              disabledReason={blockedReason ?? undefined}
              onClick={() => openCreate(activeKind)}
            >
              {t('automationAuthoring.commands.create', {
                kind: t(COMMAND_KIND_SINGULAR_KEYS[activeKind]),
              })}
            </Button>
          ) : null}
        </div>
        {activeButtonNoticeKey === null ? null : (
          <Notice
            tone={activeButtonLimitReached ? 'warn' : 'info'}
            title={
              activeButtonLimitReached
                ? t('automationAuthoring.commands.buttonLimitTitle', {
                    limit: AUTOMATION_ACTIVE_BUTTON_LIMIT,
                    kind: t(COMMAND_KIND_SINGULAR_KEYS[activeKind]),
                  })
                : t('automationAuthoring.commands.buttonLimitDefaultTitle')
            }
            body={t(activeButtonNoticeKey, {
              limit: AUTOMATION_ACTIVE_BUTTON_LIMIT,
            })}
          />
        )}

        {checking ? (
          <Notice tone="info" body={t('automationAuthoring.commands.checking')} />
        ) : null}

        {!checking && commandRows.length === 0 ? (
          <div className="rounded-card border border-border-strong bg-surface-raised p-3">
            <p className="text-card font-medium text-ink">
              {t('automationAuthoring.commands.emptyTitle', {
                kind: t(COMMAND_KIND_SINGULAR_KEYS[activeKind]),
              })}
            </p>
            <p className="mt-1 text-sm text-ink-muted">{t(STARTER_KEYS[activeKind])}</p>
          </div>
        ) : null}

        {commandRows.map((command) => {
          const unsupportedTrigger = unsupportedTriggerLine(command);
          return (
            <article
              key={command.id}
              className="flex flex-col gap-2 rounded-card border border-border-strong bg-surface-raised p-3"
              aria-label={t('automationAuthoring.commands.rowLabel', {
                name: command.name,
              })}
            >
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <h5 className="text-card font-semibold text-ink">{command.name}</h5>
                  <p className="break-words text-sm text-ink-muted">
                    {command.plainText}
                  </p>
                </div>
                <span className="shrink-0 rounded-card bg-surface px-2 py-0.5 text-xs text-ink-muted">
                  {command.enabled
                    ? t('automationAuthoring.commands.enabled')
                    : t('automationAuthoring.commands.disabled')}
                </span>
              </div>

              {command.isBroken ? (
                <Notice
                  tone="bad"
                  title={t('automationAuthoring.commands.brokenTitle')}
                  body={t('automationAuthoring.commands.brokenBody')}
                />
              ) : null}
              {unsupportedTrigger === null ? null : (
                <Notice
                  tone="warn"
                  title={t('automationAuthoring.editor.trigger.unsupportedTitle')}
                  body={t(unsupportedTrigger.key, unsupportedTrigger.values)}
                />
              )}
              {rowErrors[command.id] !== undefined ? (
                <Notice tone="bad" body={rowErrors[command.id]} />
              ) : null}

              <div className="flex flex-wrap gap-2">
                {managementEnabled ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      aria-label={t('automationAuthoring.commands.editLabel', {
                        name: command.name,
                      })}
                      onClick={() => openEdit(command)}
                    >
                      {t('automationAuthoring.commands.edit')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      aria-label={t(
                        command.enabled
                          ? 'automationAuthoring.commands.disableLabel'
                          : 'automationAuthoring.commands.enableLabel',
                        { name: command.name },
                      )}
                      disabled={
                        (!command.enabled && command.isBroken) ||
                        (!command.enabled &&
                          isButtonKind(command.commandKind) &&
                          command.commandKind === activeKind &&
                          (activeButtonOwnerCounts.get(command.createdByUserId) ?? 0) >=
                            AUTOMATION_ACTIVE_BUTTON_LIMIT)
                      }
                      disabledReason={
                        !command.enabled && command.isBroken
                          ? t('automationAuthoring.commands.brokenDisabledReason')
                          : !command.enabled &&
                              isButtonKind(command.commandKind) &&
                              command.commandKind === activeKind &&
                              (activeButtonOwnerCounts.get(command.createdByUserId) ??
                                0) >= AUTOMATION_ACTIVE_BUTTON_LIMIT
                            ? rowLimitReason(command.commandKind)
                            : undefined
                      }
                      busy={toggleBusyId === command.id}
                      onClick={() => onToggleEnabled(command.id, !command.enabled)}
                    >
                      {command.enabled
                        ? t('automationAuthoring.commands.disable')
                        : t('automationAuthoring.commands.enable')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      aria-label={t('automationAuthoring.commands.copyLabel', {
                        name: command.name,
                      })}
                      busy={duplicateBusyId === command.id}
                      onClick={() => openCopy(command)}
                    >
                      {t('automationAuthoring.commands.copy')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      aria-label={t('automationAuthoring.commands.openLogLabel', {
                        name: command.name,
                      })}
                      onClick={() => onOpenLog(command)}
                    >
                      {t('automationAuthoring.commands.openLog')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="quiet"
                      className="text-bad"
                      aria-label={t('automationAuthoring.commands.deleteLabel', {
                        name: command.name,
                      })}
                      busy={deleteBusyId === command.id}
                      onClick={() => setDeleteTarget(command)}
                    >
                      {t('automationAuthoring.commands.delete')}
                    </Button>
                  </>
                ) : null}
                {!managementEnabled ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    aria-label={t('automationAuthoring.commands.openLogLabel', {
                      name: command.name,
                    })}
                    onClick={() => onOpenLog(command)}
                  >
                    {t('automationAuthoring.commands.openLog')}
                  </Button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>

      <div
        id="automation-view-activity"
        role="tabpanel"
        hidden={activeView !== 'activity'}
        className={activeView === 'activity' ? 'flex flex-col gap-3' : 'hidden'}
      >
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-semibold text-ink">
            {t('automationAuthoring.activity.heading')}
          </h4>
          <p className="text-sm text-ink-muted">
            {t('automationAuthoring.activity.description')}
          </p>
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm font-medium text-ink">
            {t('automationAuthoring.activity.boardLabel')}
            <select
              value={activityBoardId ?? ''}
              onChange={(event) =>
                onActivityBoardChange(
                  event.currentTarget.value === '' ? null : event.currentTarget.value,
                )
              }
              className="h-9 rounded-card border border-border-strong bg-surface px-2 text-sm font-normal text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            >
              <option value="">{t('automationAuthoring.activity.allBoards')}</option>
              {activityBoardOptions.map((board) => (
                <option key={board.id} value={board.id}>
                  {board.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium text-ink">
            {t('automationAuthoring.activity.outcomeLabel')}
            <select
              value={activityOutcome ?? ''}
              onChange={(event) =>
                onActivityOutcomeChange(
                  event.currentTarget.value === ''
                    ? null
                    : (event.currentTarget.value as AutomationRunOutcome),
                )
              }
              className="h-9 rounded-card border border-border-strong bg-surface px-2 text-sm font-normal text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            >
              {OUTCOME_FILTERS.map((option) => (
                <option key={option.value ?? 'all'} value={option.value ?? ''}>
                  {t(option.key)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {activityLoading ? (
          <Notice tone="info" body={t('automationAuthoring.activity.loading')} />
        ) : null}
        {activityError !== undefined ? (
          <Notice
            tone="bad"
            title={t('automationAuthoring.activity.loadFailedTitle')}
            body={activityError}
            action={
              onRetryActivity === undefined ? undefined : (
                <Button type="button" size="sm" onClick={onRetryActivity}>
                  {t('automationAuthoring.retry')}
                </Button>
              )
            }
          />
        ) : null}
        {!activityLoading &&
        activityError === undefined &&
        activity?.entries.length === 0 ? (
          <div className="rounded-card border border-border-strong bg-surface-raised p-3">
            <p className="text-card font-medium text-ink">
              {t('automationAuthoring.activity.emptyTitle')}
            </p>
            <p className="mt-1 text-sm text-ink-muted">
              {t('automationAuthoring.activity.emptyBody')}
            </p>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          {activity?.entries.map((entry) => {
            const actor = activityEntryActor(entry, automationActivityMembers);
            return (
              <article
                key={entry.id}
                className="rounded-card border border-border-strong bg-surface-raised p-3"
                aria-label={t('automationAuthoring.activity.rowLabel', {
                  name: entry.command.name,
                })}
              >
                <div className="flex flex-wrap items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <h5 className="text-card font-semibold text-ink">
                      {entry.command.name}
                    </h5>
                    <p className="text-sm text-ink-muted">
                      {entry.board.name} - {t(ENTRY_KIND_KEYS[entry.entryKind])}
                    </p>
                  </div>
                  <span
                    className={`rounded-card px-2 py-0.5 text-xs ${
                      activityEntryTone(entry) === 'bad'
                        ? 'bg-bad/10 text-bad'
                        : activityEntryTone(entry) === 'warn'
                          ? 'bg-warn/10 text-warn'
                          : 'bg-surface text-ink-muted'
                    }`}
                  >
                    {entry.outcome === null
                      ? t('automationAuthoring.outcome.recorded')
                      : t(OUTCOME_KEYS[entry.outcome])}
                  </span>
                </div>
                <p className="mt-1 text-xs text-ink-muted">
                  <RelativeTime value={entry.occurredAt} format="long" />
                </p>
                <p className="mt-1 text-xs text-ink-muted">
                  {t(AUTOMATION_COMMAND_LABEL_KEYS[entry.command.commandKind])} -{' '}
                  {t(activityEntryTarget(entry).key, activityEntryTarget(entry).values)}
                </p>
                {actor === null ? null : (
                  <p className="mt-1 text-xs text-ink-muted">
                    {t('automationAuthoring.activity.actor', {
                      actor:
                        'displayName' in actor
                          ? actor.displayName
                          : t(actor.key, actor.values),
                    })}
                  </p>
                )}
                {entry.errorSummary !== null ? (
                  <p className="mt-2 text-sm text-ink">{entry.errorSummary}</p>
                ) : null}
              </article>
            );
          })}
        </div>
      </div>

      <div
        id="automation-view-usage"
        role="tabpanel"
        hidden={activeView !== 'usage'}
        className={activeView === 'usage' ? 'flex flex-col gap-3' : 'hidden'}
      >
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-semibold text-ink">
            {t('automationAuthoring.usage.heading')}
          </h4>
          <p className="text-sm text-ink-muted">
            {t('automationAuthoring.usage.description')}
          </p>
        </div>
        {/*
          ⚠ THE BRANCH READS `kind`, NEVER THE RENDERED SENTENCE. It used to
          compare the title against the English `'Monthly run quota exhausted'`,
          which routing would have turned into a silently dead branch — the
          `role="alert"` `Notice` gone, failing as a missing element.
        */}
        {quotaSummaryView?.kind === 'runsExhausted' ? (
          <Notice
            tone="bad"
            title={t('automationAuthoring.usage.runsExhaustedTitle')}
            body={
              quotaResetText === null
                ? t('automationAuthoring.usage.exhaustedNoticeBody')
                : t('automationAuthoring.usage.exhaustedNoticeBodyWithReset', {
                    reset: quotaResetText,
                  })
            }
          />
        ) : null}
        {usage !== null ? (
          <div className="grid gap-2">
            {quotaMetricRows.map((metric) => (
              <article
                key={metric.id}
                aria-label={t('automationAuthoring.usage.metricArticle', {
                  metric: t(QUOTA_METRIC_KEYS[metric.id]),
                })}
                className={`rounded-card border p-3 ${quotaMetricTone(metric)}`}
              >
                <h5 className="text-xs font-semibold uppercase text-ink-muted">
                  {t('automationAuthoring.usage.metricConsumed', {
                    metric: t(QUOTA_METRIC_KEYS[metric.id]),
                  })}
                </h5>
                <p className="mt-1 text-xl font-semibold text-ink">
                  {formatCounter(metric.used)} / {formatCounter(metric.limit)}
                </p>
                <p className="mt-1 text-xs text-ink-muted">
                  {t('automationAuthoring.usage.metricRemaining', {
                    remaining: formatCounter(metric.remaining),
                  })}
                </p>
                <div
                  role="meter"
                  aria-label={t('automationAuthoring.usage.metricMeter', {
                    metric: t(QUOTA_METRIC_KEYS[metric.id]),
                  })}
                  aria-valuemin={0}
                  aria-valuemax={metric.limit}
                  aria-valuenow={Math.min(metric.used, metric.limit)}
                  className="mt-2 h-2 overflow-hidden rounded-card bg-surface"
                >
                  <div
                    className={
                      metric.exhausted
                        ? 'h-full bg-bad'
                        : metric.warning
                          ? 'h-full bg-warn'
                          : 'h-full bg-ok'
                    }
                    style={{ width: `${metric.percent}%` }}
                  />
                </div>
                {metric.exhausted ? (
                  <p className="mt-2 text-xs font-semibold">
                    {t('automationAuthoring.usage.metricExhausted')}
                  </p>
                ) : metric.warning ? (
                  <p className="mt-2 text-xs font-semibold">
                    {t('automationAuthoring.usage.metricRunningLow')}
                  </p>
                ) : null}
              </article>
            ))}
            <p className="text-xs text-ink-muted">
              {t('automationAuthoring.usage.period', {
                start: usage.periodStart,
                end: usage.periodEnd,
                reset: quotaResetText ?? '',
              })}
            </p>
          </div>
        ) : null}
      </div>

      <div
        id="automation-view-admin"
        role="tabpanel"
        hidden={activeView !== 'admin'}
        className={activeView === 'admin' ? 'flex flex-col gap-3' : 'hidden'}
      >
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-semibold text-ink">
            {t('automationAuthoring.admin.heading')}
          </h4>
          <p className="text-sm text-ink-muted">
            {t('automationAuthoring.admin.description')}
          </p>
        </div>

        {!canAdminReview ? (
          <Notice
            tone="locked"
            body={adminDisabledReason ?? t('automationAuthoring.admin.lockedBody')}
          />
        ) : null}

        <label className="flex flex-col gap-1 text-sm font-medium text-ink">
          {t('automationAuthoring.admin.boardLabel')}
          <select
            value={adminBoardId ?? ''}
            disabled={!canAdminReview}
            onChange={(event) =>
              onAdminBoardChange?.(
                event.currentTarget.value === '' ? null : event.currentTarget.value,
              )
            }
            className="h-9 rounded-card border border-border-strong bg-surface px-2 text-sm font-normal text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-60"
          >
            <option value="">{t('automationAuthoring.admin.allBoards')}</option>
            {adminBoardOptions.map((board) => (
              <option key={board.id} value={board.id}>
                {board.name}
              </option>
            ))}
          </select>
        </label>

        {adminReviewLoading ? (
          <Notice tone="info" body={t('automationAuthoring.admin.loading')} />
        ) : null}
        {adminReviewError !== undefined ? (
          <Notice
            tone="bad"
            title={t('automationAuthoring.admin.loadFailedTitle')}
            body={adminReviewError}
            action={
              onRetryAdminReview === undefined ? undefined : (
                <Button type="button" size="sm" onClick={onRetryAdminReview}>
                  {t('automationAuthoring.retry')}
                </Button>
              )
            }
          />
        ) : null}

        {!adminReviewLoading &&
        adminReviewError === undefined &&
        adminReview?.commands.length === 0 ? (
          <div className="rounded-card border border-border-strong bg-surface-raised p-3">
            <p className="text-card font-medium text-ink">
              {t('automationAuthoring.admin.emptyTitle')}
            </p>
            <p className="mt-1 text-sm text-ink-muted">
              {t('automationAuthoring.admin.emptyBody')}
            </p>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          {adminReview?.commands.map((command) => (
            <article
              key={command.id}
              className="rounded-card border border-border-strong bg-surface-raised p-3"
              aria-label={t('automationAuthoring.admin.rowLabel', {
                name: command.name,
              })}
            >
              <div className="flex flex-wrap items-start gap-2">
                <div className="min-w-0 flex-1">
                  <h5 className="text-card font-semibold text-ink">{command.name}</h5>
                  <p className="break-words text-sm text-ink-muted">
                    {command.board.name} -{' '}
                    {t(AUTOMATION_COMMAND_LABEL_KEYS[command.commandKind])}
                  </p>
                </div>
                <span
                  className={`rounded-card px-2 py-0.5 text-xs ${
                    command.enabled
                      ? 'bg-ok/10 text-ok'
                      : command.diagnostics?.reason === 'admin_disabled'
                        ? 'bg-warn/10 text-warn'
                        : 'bg-surface text-ink-muted'
                  }`}
                >
                  {command.enabled
                    ? t('automationAuthoring.admin.enabled')
                    : t('automationAuthoring.admin.disabled')}
                </span>
              </div>
              <dl className="mt-2 grid gap-1 text-xs text-ink-muted">
                {/*
                  ⚠ The four `<dt>`s shipped with a TRAILING SPACE inside the
                  element. No catalogue value ends in whitespace, so the space
                  becomes `{' '}` beside the routed word — byte-identical output.
                */}
                <div>
                  <dt className="inline font-medium text-ink">
                    {t('automationAuthoring.admin.createdBy')}{' '}
                  </dt>
                  <dd className="inline">
                    {t(
                      userFallbackLine(command.createdByUserId).key,
                      userFallbackLine(command.createdByUserId).values,
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="inline font-medium text-ink">
                    {t('automationAuthoring.admin.actsAs')}{' '}
                  </dt>
                  <dd className="inline">
                    {t(
                      userFallbackLine(command.actingUserId).key,
                      userFallbackLine(command.actingUserId).values,
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="inline font-medium text-ink">
                    {t('automationAuthoring.admin.scope')}{' '}
                  </dt>
                  <dd className="inline">
                    {command.scopeKind === 'all_administrable_boards'
                      ? t('automationAuthoring.admin.allAdministrableBoards')
                      : command.board.name}
                  </dd>
                </div>
                {command.lastRunAt !== null ? (
                  <div>
                    <dt className="inline font-medium text-ink">
                      {t('automationAuthoring.admin.lastRun')}{' '}
                    </dt>
                    <dd className="inline">
                      <RelativeTime value={command.lastRunAt} format="long" />
                      {command.lastOutcome === null
                        ? ''
                        : ` - ${t(OUTCOME_KEYS[command.lastOutcome])}`}
                    </dd>
                  </div>
                ) : null}
              </dl>
              {command.diagnostics !== null ? (
                <Notice
                  tone={command.diagnostics.reason === 'admin_disabled' ? 'warn' : 'bad'}
                  title={t('automationAuthoring.diagnostic.title', {
                    reason: t(DIAGNOSTIC_REASON_KEYS[command.diagnostics.reason]),
                  })}
                  body={command.diagnostics.detailText}
                />
              ) : null}
              {adminRowErrors[command.id] !== undefined ? (
                <Notice tone="bad" body={adminRowErrors[command.id]} />
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  aria-label={t('automationAuthoring.admin.viewDefinitionLabel', {
                    name: command.name,
                  })}
                  onClick={() => onOpenAdminDefinition?.(command)}
                >
                  {t('automationAuthoring.admin.viewDefinition')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  aria-label={t('automationAuthoring.admin.openLogLabel', {
                    name: command.name,
                  })}
                  onClick={() => onOpenLog(command)}
                >
                  {t('automationAuthoring.admin.openLog')}
                </Button>
                {command.enabled ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    aria-label={t('automationAuthoring.admin.disableLabel', {
                      name: command.name,
                    })}
                    busy={adminDisableBusyId === command.id}
                    onClick={() => openAdminDisable(command)}
                  >
                    {t('automationAuthoring.admin.disableAction')}
                  </Button>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>

      <Dialog
        open={editorOpen}
        onOpenChange={(open) => {
          if (!open) closeEditor();
        }}
        title={
          editorCommand === null
            ? t('automationAuthoring.editorDialog.createTitle', {
                kind:
                  editorKind === null
                    ? t('automationAuthoring.editorDialog.fallbackKind')
                    : t(COMMAND_KIND_SINGULAR_KEYS[editorKind]),
              })
            : t('automationAuthoring.editorDialog.editTitle', {
                name: editorCommand.name,
              })
        }
        description={t('automationAuthoring.editorDialog.description')}
      >
        {editorKind !== null ? (
          <AutomationEditor
            boardId={boardId}
            currentUserId={currentUserId}
            lists={lists}
            labels={labels}
            members={members}
            commandKind={editorKind}
            command={editorCommand}
            busy={editorBusy}
            error={editorSubmitted ? editorError : undefined}
            activeButtonLimitNotice={
              editorKind === null ? undefined : rowLimitReason(editorKind)
            }
            onSubmit={submitEditor}
            onCancel={closeEditor}
          />
        ) : null}
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t('automationAuthoring.delete.title')}
        description={
          deleteTarget === null
            ? undefined
            : t('automationAuthoring.delete.description', { name: deleteTarget.name })
        }
      >
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="danger"
            busy={deleteTarget !== null && deleteBusyId === deleteTarget.id}
            onClick={() => {
              if (deleteTarget !== null) {
                onDelete(deleteTarget.id);
                setDeleteTarget(null);
              }
            }}
          >
            {t('automationAuthoring.delete.confirm')}
          </Button>
          <Button type="button" variant="secondary" onClick={() => setDeleteTarget(null)}>
            {t('automationAuthoring.delete.cancel')}
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={copyTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeCopy();
        }}
        title={
          copyTarget === null
            ? t('automationAuthoring.copy.title')
            : t('automationAuthoring.copy.titleNamed', { name: copyTarget.name })
        }
        description={t('automationAuthoring.copy.description')}
      >
        {copyTarget === null ? null : (
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-1 text-sm font-medium text-ink">
              {t('automationAuthoring.copy.nameLabel')}
              <input
                value={copyName}
                onChange={(event) => setCopyName(event.currentTarget.value)}
                placeholder={t('automationAuthoring.copy.namePlaceholder', {
                  name: copyTarget.name,
                })}
                className="h-9 rounded-card border border-border-strong bg-surface px-2 text-sm font-normal text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
              />
            </label>

            <label className="flex flex-col gap-1 text-sm font-medium text-ink">
              {t('automationAuthoring.copy.destinationLabel')}
              <select
                value={copyDestinationBoardId}
                onChange={(event) => setCopyDestinationBoardId(event.currentTarget.value)}
                className="h-9 rounded-card border border-border-strong bg-surface px-2 text-sm font-normal text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
              >
                <option value="">{t('automationAuthoring.copy.thisBoard')}</option>
                {destinationBoards
                  .filter((board) => board.id !== boardId)
                  .map((board) => (
                    <option key={board.id} value={board.id}>
                      {board.title}
                    </option>
                  ))}
              </select>
            </label>

            {duplicateResult !== null ? (
              <Notice
                tone={duplicateResult.unresolvedReferences.length > 0 ? 'warn' : 'info'}
                title={t('automationAuthoring.copy.doneTitle', {
                  name: duplicateResult.command.name,
                })}
                body={
                  duplicateResult.unresolvedReferences.length > 0
                    ? t('automationAuthoring.copy.unresolvedBody')
                    : t('automationAuthoring.copy.unchangedBody')
                }
              />
            ) : null}

            {duplicateResult?.unresolvedReferences.length ? (
              <ul className="flex flex-col gap-2 text-sm text-ink-muted">
                {duplicateResult.unresolvedReferences.map((reference, index) => (
                  <li
                    key={`${reference.referenceKind}-${reference.sourceId ?? index}-${reference.actionPosition ?? 'none'}`}
                    className="rounded-card border border-border bg-surface-raised p-2"
                  >
                    <span className="font-medium text-ink">
                      {t(REFERENCE_KIND_KEYS[reference.referenceKind])}
                    </span>
                    {': '}
                    {reference.sourceName ??
                      reference.sourceId ??
                      t('automationAuthoring.copy.unresolvedFallback')}
                    {' - '}
                    {t(UNRESOLVED_REASON_KEYS[reference.reason])}
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="primary"
                busy={duplicateBusyId === copyTarget.id}
                onClick={submitCopy}
              >
                {t('automationAuthoring.copy.submit')}
              </Button>
              <Button type="button" variant="secondary" onClick={closeCopy}>
                {t('automationAuthoring.copy.close')}
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        open={adminDefinitionTarget !== null}
        onOpenChange={(open) => {
          if (!open) onCloseAdminDefinition?.();
        }}
        title={
          adminDefinitionTarget === null
            ? t('automationAuthoring.definition.title')
            : t('automationAuthoring.definition.titleNamed', {
                name: adminDefinitionTarget.name,
              })
        }
        description={t('automationAuthoring.definition.description')}
      >
        <div className="flex flex-col gap-3">
          {adminDefinitionLoading ? (
            <Notice tone="info" body={t('automationAuthoring.definition.loading')} />
          ) : null}
          {adminDefinitionError !== undefined ? (
            <Notice
              tone="bad"
              title={t('automationAuthoring.definition.loadFailedTitle')}
              body={adminDefinitionError}
              action={
                onRetryAdminDefinition === undefined ? undefined : (
                  <Button type="button" size="sm" onClick={onRetryAdminDefinition}>
                    {t('automationAuthoring.retry')}
                  </Button>
                )
              }
            />
          ) : null}
          {adminDefinition !== null ? (
            <div className="flex flex-col gap-3">
              <div className="rounded-card border border-border-strong bg-surface-raised p-3">
                <p className="text-card font-semibold text-ink">{adminDefinition.name}</p>
                <p className="mt-1 break-words text-sm text-ink-muted">
                  {adminDefinition.plainText}
                </p>
              </div>
              <div className="grid gap-2 text-sm text-ink-muted">
                <p>
                  <span className="font-medium text-ink">
                    {t('automationAuthoring.definition.kind')}
                  </span>{' '}
                  {t(AUTOMATION_COMMAND_LABEL_KEYS[adminDefinition.commandKind])}
                </p>
                <p>
                  <span className="font-medium text-ink">
                    {t('automationAuthoring.definition.actions')}
                  </span>{' '}
                  {adminDefinition.actions.length}
                </p>
                <p>
                  <span className="font-medium text-ink">
                    {t('automationAuthoring.definition.visibility')}
                  </span>{' '}
                  {t(VISIBILITY_KEYS[adminDefinition.visibility])}
                </p>
                <p>
                  <span className="font-medium text-ink">
                    {t('automationAuthoring.definition.state')}
                  </span>{' '}
                  {adminDefinition.enabled
                    ? t('automationAuthoring.definition.enabled')
                    : t('automationAuthoring.definition.disabled')}
                </p>
              </div>
              <ul className="flex flex-col gap-2">
                {adminDefinition.actions.map((action) => (
                  <li
                    key={action.id}
                    className="rounded-card border border-border bg-surface p-2 text-sm text-ink"
                  >
                    {/*
                      ⚠ ONE BYTE CHANGE, RECORDED: this list printed the raw wire
                      value (`http request`) while the editor beside it said
                      `HTTP request`. Reading the editor's own table makes the two
                      agree; nothing in `src/` or `e2e/` bound the lower-case
                      spelling.
                    */}
                    {t(ACTION_LABEL_KEYS[action.actionKind])}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </Dialog>

      <Dialog
        open={adminDisableTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeAdminDisable();
        }}
        title={t('automationAuthoring.adminDisable.title')}
        description={
          adminDisableTarget === null
            ? undefined
            : t('automationAuthoring.adminDisable.description', {
                name: adminDisableTarget.name,
              })
        }
      >
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1 text-sm font-medium text-ink">
            {t('automationAuthoring.adminDisable.reasonLabel')}
            <textarea
              value={adminDisableReason}
              onChange={(event) => setAdminDisableReason(event.currentTarget.value)}
              rows={3}
              placeholder={t('automationAuthoring.adminDisable.reasonPlaceholder')}
              className="min-h-24 rounded-card border border-border-strong bg-surface px-2 py-2 text-sm font-normal text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="danger"
              busy={
                adminDisableTarget !== null &&
                adminDisableBusyId === adminDisableTarget.id
              }
              onClick={submitAdminDisable}
            >
              {t('automationAuthoring.adminDisable.confirm')}
            </Button>
            <Button type="button" variant="secondary" onClick={closeAdminDisable}>
              {t('automationAuthoring.adminDisable.cancel')}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={logCommand !== null}
        onOpenChange={(open) => {
          if (!open) onCloseLog();
        }}
        title={
          logCommand === null
            ? t('automationAuthoring.log.title')
            : t('automationAuthoring.log.titleNamed', { name: logCommand.name })
        }
        description={t('automationAuthoring.log.description')}
      >
        <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto pr-1">
          {logLoading ? (
            <Notice tone="info" body={t('automationAuthoring.log.loading')} />
          ) : null}
          {logError !== undefined ? <Notice tone="bad" body={logError} /> : null}

          {log?.command.diagnostics ? (
            <Notice
              tone="bad"
              title={t('automationAuthoring.diagnostic.title', {
                reason: t(DIAGNOSTIC_REASON_KEYS[log.command.diagnostics.reason]),
              })}
              body={log.command.diagnostics.detailText}
            />
          ) : null}

          {log !== null ? (
            <p className="text-xs text-ink-muted">
              {/*
                ⚠ SPLIT AROUND AN ELEMENT, and the count is the third argument.
                `formatRelative` is not exported from `RelativeTime.tsx` and that
                file is not this slice's, so the key holds everything up to the
                element and the full stop after it stays a JSX literal rather
                than becoming a one-character catalogue entry.
              */}
              {t(
                'automationAuthoring.log.retention',
                { days: log.retention.windowDays },
                log.retention.windowDays,
              )}{' '}
              <RelativeTime value={log.retention.since} format="long" />.
            </p>
          ) : null}

          {log?.runState === 'never_run' ? (
            <div className="rounded-card border border-border-strong bg-surface-raised p-3">
              <p className="text-card font-medium text-ink">
                {t('automationAuthoring.log.neverRunTitle')}
              </p>
              <p className="mt-1 text-sm text-ink-muted">
                {t('automationAuthoring.log.neverRunBody')}
              </p>
            </div>
          ) : null}

          {log?.entries.map((entry) => {
            const actor = entryActor(entry, members);
            const target = entryTarget(entry);
            return (
              <article
                key={entry.id}
                className="rounded-card border border-border-strong bg-surface-raised p-3"
              >
                {/*
                  ⚠ BOTH LINES ARE OPTIONAL, AND AN ABSENT ONE IS THE POINT.
                  The route names the card and the actor; where it withholds a
                  name — a card that has moved to a board this reader cannot
                  see — the line is omitted rather than degraded to a ULID.
                  `FE-70.2` is precisely the end of the id-in-a-sentence-slot,
                  so "no name to show" renders as no line.
                */}
                <div className="flex flex-wrap items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <h5 className="text-card font-semibold text-ink">
                      {t(ENTRY_KIND_KEYS[entry.entryKind])}
                    </h5>
                    {target !== null ? (
                      <p className="text-sm text-ink-muted">
                        {t(target.key, target.values)}
                      </p>
                    ) : null}
                  </div>
                  <span
                    className={`rounded-card px-2 py-0.5 text-xs ${
                      entryTone(entry) === 'bad'
                        ? 'bg-bad/10 text-bad'
                        : entryTone(entry) === 'warn'
                          ? 'bg-warn/10 text-warn'
                          : 'bg-surface text-ink-muted'
                    }`}
                  >
                    {entry.outcome === null
                      ? t('automationAuthoring.outcome.recorded')
                      : t(OUTCOME_KEYS[entry.outcome])}
                  </span>
                </div>
                <p className="mt-1 text-xs text-ink-muted">
                  <RelativeTime value={entry.occurredAt} format="long" />
                </p>
                {actor !== null ? (
                  <p className="mt-1 text-xs text-ink-muted">
                    {t(entryActorLineKey(entry), {
                      actor:
                        'displayName' in actor
                          ? actor.displayName
                          : t(actor.key, actor.values),
                    })}
                  </p>
                ) : null}
                {entry.detailText !== null ? (
                  <p className="mt-2 text-sm text-ink">{entry.detailText}</p>
                ) : null}
                {entry.entryKind === 'cap_exceeded' ? (
                  <Notice
                    tone="warn"
                    title={t('automationAuthoring.log.capTitle')}
                    body={t('automationAuthoring.log.capBody')}
                  />
                ) : null}
                {entry.entryKind === 'loop_suppressed' ? (
                  <Notice
                    tone="warn"
                    title={t('automationAuthoring.log.loopTitle')}
                    body={t('automationAuthoring.log.loopBody', {
                      limit: AUTOMATION_CHAIN_DEPTH_LIMIT,
                    })}
                  />
                ) : null}
                {entry.actions.length > 0 ? (
                  <ul className="mt-3 flex flex-col gap-2">
                    {entry.actions.map((action) => {
                      const statusLine = actionStatusLine(action);
                      return (
                        <li
                          key={action.id}
                          className="rounded-card border border-border bg-surface p-2 text-sm"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-ink">{action.summary}</span>
                            <span
                              className={
                                action.succeeded
                                  ? 'text-xs text-ok'
                                  : 'text-xs font-medium text-bad'
                              }
                            >
                              {action.succeeded
                                ? t('automationAuthoring.log.actionSucceeded')
                                : t('automationAuthoring.log.actionFailed')}
                            </span>
                            {/*
                            ⚠ `FE-71.3` — THE DECLARED FIELD, NOT THE SENTENCE.
                            `FR-AUTO-022` AC4 asks for the failure to be
                            recorded *"with the status returned"*, and until
                            now the status reached a person only inside the
                            English `error_text` sentence
                            `HTTP request returned 503.` — parsed back out by
                            the shared codec. `actions[].httpStatus` is the
                            structured fact, and it is what renders here;
                            nothing on this line re-reads `errorText`.

                            ⚠ THE GUARD IS `typeof === 'number'`, NOT
                            `!== null`. The client parses with the PERMISSIVE
                            twin, where the field is `.optional()`, so the
                            value is `number | null | undefined` — and
                            `undefined !== null` is TRUE, which would render
                            the phrase with an empty slot and throw out of
                            `interfaceMessage`.

                            ⚠ AN ABSENT STATUS IS RENDERED AS ABSENT, and a
                            200 is never invented. Successful HTTP actions
                            store the returned status in the same snapshot
                            field failures use; rows with no status still show
                            no status phrase.
                          */}
                            {statusLine !== null ? (
                              <span className="text-xs text-ink-muted">
                                {t(statusLine.key, statusLine.values)}
                              </span>
                            ) : null}
                          </div>
                          {action.errorText !== null ? (
                            <p className="mt-1 text-ink-muted">{action.errorText}</p>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </article>
            );
          })}

          {/*
            ⚠ `FE-70.2` — THE AFFORDANCE FOR A CURSOR THAT WAS ALREADY
            HONOURED. `GET /automations/:id/log` accepts `cursor` and computes
            `nextCursor` from the last row kept; until now the page-two the
            server was willing to serve had no way of being asked for.

            ⚠ **RENDERED ON `nextCursor !== null` ALONE.** Never on
            `entries.length`: a full page can be the last page, and a short page
            can have a successor. `log?.nextCursor !== null` would be TRUE with
            no log loaded at all, so the cursor is read into a local first.

            ⚠ **NOTHING HERE CONSTRUCTS A CURSOR.** The codec is keyset over
            `(command_id, occurred_at)` and a fabricated cursor is refused with
            a 422 — `apps/web/e2e/FR-AUTO-013.spec.ts` pins that arm over HTTP.
            The click carries no argument at all; `BoardMenuPanel` holds the
            cursor the server returned and sends it back verbatim.

            ⚠ **THE FAILURE NOTICE IS OUTSIDE THE CURSOR GUARD**, so a refused
            page is stated even if the cursor it was refused for is gone. An
            affordance that swallows the 422 turns a refusal into an empty
            page, which is the lies-rather-than-omits shape one level up.

            ⚠ **THE BUTTON IS NOT GATED ON `onLoadOlder` BEING WIRED.** Gating
            it would let this file's own tests pass a handler and go green
            while the panel never passed one and the affordance stayed dark
            with nothing red — the exact silent failure this project keeps
            recording. A missing wire is visible on screen instead.
          */}
          {olderError !== undefined ? (
            <Notice
              tone="bad"
              title={t('automationAuthoring.log.olderFailed')}
              body={olderError}
            />
          ) : null}
          {olderCursor !== null ? (
            <Button
              type="button"
              variant="secondary"
              busy={olderLoading}
              busyLabel={t('automationAuthoring.log.loadingOlder')}
              onClick={() => {
                onLoadOlder?.();
              }}
            >
              {t('automationAuthoring.log.loadOlder')}
            </Button>
          ) : null}
        </div>
      </Dialog>
    </section>
  );
}
